import type { Core, WebViewerInstance } from '@pdftron/webviewer';
import { BroadcastChannel } from 'broadcast-channel';
import {
    canEditAnnot,
    devError,
    devLog,
    devWarning,
    getVector,
    intColor,
    isOwner,
    readFromClipboard,
    roundDecimals,
    toCompareColor,
    toCSSColor,
    writeToClipboard,
} from '../utils';
import { ReduxUpdater } from './ReduxUpdater';
import type {
    IPresetSelections,
    IPermission,
    IFontItem,
    ILocation,
    StampCategory,
    IUserStamp,
} from '../types';
import type { EmbeddedFileAttachmentMeta } from '../components/Layout/Sidebar/Elements/Attachments';
import { ApryseUpdater } from './ApryseUpdater';
import { GlobalVariables } from './GlobalVariables';
import { GlobalDialogs } from './GlobalDialogs';
import { PdfViewerViewModeFlag } from './Enums';
import { backendApiUrl, handleDeleteKey } from '../App';
import { CustomHistoryManager, type ISpecificChangeInfo } from './CustomHistoryManager';
import i18next from 'i18next';
import { RedliningActionLogger } from './RedliningActionLogger';
import { ToastSeverity } from '../components/General/Toast/Toast';
import { GlobalConstants } from './GlobalConstants';
import { parseXFDFDoc, serializeXFDFDoc } from '../utils/xfdfUtils';
import { v4 as uuidv4 } from 'uuid';

export type MeasurementAnnotation =
    | Core.Annotations.LineAnnotation
    | Core.Annotations.PolylineAnnotation
    | Core.Annotations.PolygonAnnotation;

export const FreeTextIntents = {
    Freetext: 'FreeText',
    Callout: 'FreeTextCallout',
    Typewriter: 'FreeTextTypeWriter',
};

export interface ISignatureData {
    contactInfo?: string;
    date: Date;
    digsigDocPermissions?: number;
    digestAlgorithm?: number;
    digestStatus?: number;
    documentStatus?: number;
    id: number;
    isSigningTimeValid?: boolean;
    hasTrustVerificationResult?: boolean;
    location?: string;
    lockedFields?: string[];
    permissionsStatus?: number;
    reason?: string;
    signatureField: Core.PDFNet.DigitalSignatureField;
    signer: string;
    signingTime?: Date;
    timeOfTrustVerification?: Date;
    timeOfTrustVerificationENUM?: number;
    trustVerificationResult?: Core.PDFNet.TrustVerificationResult;
    unsupportedFeatures: string[];
    verificationResult: Core.PDFNet.VerificationResult;
    verificationStatus: boolean;
    verificationResultDissallowedChanges?: Core.PDFNet.DisallowedChange[];
    wasTrustVerificationResultSuccessful?: boolean;
}

type ICommonTextProperties = Core.Tools.CalloutCreateTool | Core.Tools.FreeTextCreateTool;

export class ApryseSDK {
    private static _annotationHistoryManager: Core.AnnotationHistoryManager;
    private static _annotationManager: Core.AnnotationManager;
    private static _AnnotationsClass: typeof Core.Annotations;
    private static _annotationsShiftVector: Core.Math.Point;
    private static _annotationsToBePasted: Core.Annotations.Annotation[] = [];
    private static _broadcastChannel: BroadcastChannel;
    private static _broadcastMessage: string;
    private static _canCopy = false;
    private static _canSessionPaste = false;
    private static _contentHistoryManager: Core.ContentEditHistoryManager;
    private static _createStampTool: Core.Tools.RubberStampCreateTool;
    private static _customStamps: { [id: string]: Core.Annotations.StampAnnotation } = {};
    private static _doc: Core.Document;
    private static _documentViewer: Core.DocumentViewer;
    private static _editBoxManager: Core.EditBoxManager;
    private static _instance: WebViewerInstance;
    private static _MathClass: typeof Core.Math;
    private static _pageCanvases: { [id: number]: HTMLCanvasElement } = {};
    private static _pdfDoc: Core.PDFNet.PDFDoc;
    private static _pdfNetClass: typeof Core.PDFNet;
    private static _textAnnotationCopyTextContext: Core.Annotations.Annotation | null = null;
    private static _textAnnotationEditingContext: Core.Annotations.Annotation | null = null;
    private static _ToolsClass: typeof Core.Tools;
    private static _undoManager: Core.PDFNet.UndoManager;
    private static _signatureData: { [id: number]: ISignatureData } = {};
    static _startTime = 0;
    static _documentLoaded = false;
    static _annotationsLoaded = false;

    private static _onViewerLanguageChanged(ev: CustomEvent): void {
        const newLanguage = ev.detail.next;
        devLog(`[Apryse SDK] language changed: "${newLanguage}"`);

        ReduxUpdater.ribbonRefresh();
        ReduxUpdater.annotationSidepanelRefresh();
    }

    private static _onViewerContextMenu(ev: MouseEvent): void {
        if (!ApryseSDK.documentViewer) {
            return;
        }

        const clickedAnnotation = ApryseSDK.documentViewer
            .getAnnotationManager()
            .getAnnotationByMouseEvent(ev);

        if (!ev.view?.frameElement) {
            return;
        }

        if (clickedAnnotation) {
            ApryseSDK.showContextMenuForAnnotation(
                ev.view?.frameElement,
                { x: ev.screenX, y: (ev.clientY + ev.screenY) / 2 },
                clickedAnnotation,
            );
            return;
        }

        GlobalDialogs.showContextMenu(ev.view?.frameElement, {
            x: ev.screenX,
            y: (ev.clientY + ev.screenY) / 2,
        });
    }

    static readonly userCategoriesList: StampCategory[] = [];

    static showContextMenuForAnnotation(
        anchor: Element,
        location: ILocation,
        annotation: Core.Annotations.Annotation,
    ) {
        // Select if it wasn't selected yet
        if (!ApryseSDK.isAnnotationSelected(annotation)) {
            this._annotationManager.selectAnnotation(annotation);
        }

        GlobalDialogs.showContextMenu(anchor, location);
    }

    /**
     * ENUMs
     */

    static get DocumentViewerEvents() {
        return this._instance.Core.DocumentViewer.Events;
    }

    static get ToolsENUM() {
        return {
            ANNOTATION_EDIT: 'AnnotationEdit',
            CREATE_CIRCLE: 'AnnotationCreateEllipse',

            CREATE_FREETEXT: 'AnnotationCreateFreeText',
            CREATE_FREETEXT_CALLOUT: 'AnnotationCreateCallout',
            CREATE_FREETEXT_TYPEWRITER: 'AnnotationCreateFreeText2',

            CREATE_ARROW: 'AnnotationCreateArrow2',
            CREATE_LINE: 'AnnotationCreateArrow',
            CREATE_MEASUREMENT_AREA: 'AnnotationCreateAreaMeasurement',
            CREATE_MEASUREMENT_DISTANCE: 'AnnotationCreateDistanceMeasurement',
            CREATE_MEASUREMENT_PERIMETER: 'AnnotationCreatePerimeterMeasurement',
            CREATE_NOTE: 'AnnotationCreateNote???',
            CREATE_PENCIL: 'AnnotationCreateFreeHand',
            CREATE_POLYGON: 'AnnotationCreatePolygon',
            CREATE_POLYGON_CLOUD: 'AnnotationCreatePolygonCloud',
            CREATE_POLYLINE: 'AnnotationCreatePolyline',
            CREATE_SQUARE: 'AnnotationCreateRectangle',
            CREATE_STAMP: 'AnnotationCreateRubberStamp',

            CREATE_TEXT_HIGHLIGHT: 'AnnotationCreateTextHighlight',
            CREATE_TEXT_STRIKEOUT: 'AnnotationCreateTextStrikeout',
            CREATE_TEXT_SQUIGGLY: 'AnnotationCreateTextSquiggly',
            CREATE_TEXT_UNDERLINE: 'AnnotationCreateTextUnderline',

            DEFAULT: 'Pan',

            MARQUEE_ZOOM_TOOL: 'MarqueeZoomTool',
            INSERT_IMAGE_TOOL: 'InsertImageTool',

            PAN: 'Pan',
            TEXT_SELECT: 'TextSelect',

            SIGNATURE: 'AnnotationCreateSignature',
            PASTE_PREVIEW: 'pasteAnnotation',
        };
    }

    /**
     * Global getters/setters
     */
    static get annotationHistoryManager(): Core.AnnotationHistoryManager {
        return this._annotationHistoryManager;
    }

    static set annotationHistoryManager(
        newAnnotationHistoryManager: Core.AnnotationHistoryManager,
    ) {
        this._annotationHistoryManager = newAnnotationHistoryManager;
        this._annotationHistoryManager.clear();
        this._annotationHistoryManager.addEventListener('historyChanged', () =>
            ApryseSDK.onAnnotationHistoryChanged(),
        );
    }

    static get annotationManager(): Core.AnnotationManager {
        return this._annotationManager;
    }

    static set annotationManager(annotationManager: Core.AnnotationManager) {
        this._annotationManager = annotationManager;
        this._annotationManager.setCurrentUser(GlobalVariables.ownersName);
        this._broadcastChannel = new BroadcastChannel(
            `${GlobalVariables.ownersName} broadcast channel`,
        );
        this._broadcastChannel.onmessage = msg => (ApryseSDK.broadcastMessage = msg);
        this._annotationManager.setPermissionCheckCallback((_author, annotation) => {
            if (!isOwner(annotation)) {
                return false;
            }
            if (['area', 'distance', 'perimeter'].includes(ApryseSDK.annotPrettyType(annotation))) {
                return annotation.Author === annotationManager.getCurrentUser();
            }
            if (ReduxUpdater.viewMode !== PdfViewerViewModeFlag.Annotation || annotation.ReadOnly) {
                return false;
            }
            return annotation.Author === annotationManager.getCurrentUser();
        });
        this._annotationManager.addEventListener('annotationSelected', this.onAnnotationSelected);
        this._annotationManager.addEventListener(
            'annotationDoubleClicked',
            this.onAnnotationDoubleClick,
        );
        this._annotationManager.addEventListener('annotationChanged', this.onAnnotationChanged);

        this._editBoxManager = annotationManager.getEditBoxManager();
        this._editBoxManager.addEventListener('editorBlur', () => {
            GlobalVariables.disableKeyboardShortcuts = false;
            GlobalVariables.isTextEditing = false;
        });

        this._editBoxManager.addEventListener('editorFocus', () => {
            GlobalVariables.disableKeyboardShortcuts = true;
            GlobalVariables.isTextEditing = true;
        });
    }

    static get AnnotationModeElements(): string[] {
        return [
            'textHighlightToolButton',
            'textStrikeoutToolButton',
            'textSquigglyToolButton',
            'textUnderlineToolButton',
            'linkButton',
        ];
    }

    static get AnnotationsClass(): typeof Core.Annotations {
        return this._AnnotationsClass;
    }

    static get annotationsShiftVector(): Core.Math.Point {
        return this._annotationsShiftVector;
    }

    static get annotationsToBePasted(): Core.Annotations.Annotation[] {
        return this._annotationsToBePasted;
    }

    static set annotationsToBePasted(annots: Core.Annotations.Annotation[]) {
        this._annotationsToBePasted = annots;
        ReduxUpdater.ribbonRefresh();
        if (this._annotationsToBePasted.length > 0) {
            this._annotationsShiftVector = ApryseSDK.rectShiftVector(
                this._annotationsToBePasted.map(annot => annot.getRect()),
            );
        }
    }

    static get attachments() {
        return this._annotationManager
            .getAnnotationsList()
            .filter(annot => annot instanceof ApryseSDK.AnnotationsClass.FileAttachmentAnnotation);
    }

    static get broadcastMessage(): string {
        return this._broadcastMessage;
    }

    static set broadcastMessage(message: string) {
        devLog('broadcastMessage', message);
        this._broadcastMessage = message;
        this.canSessionPaste = this._broadcastMessage !== '';
    }

    static get canCopy(): boolean {
        return this._canCopy;
    }

    static set canCopy(newValue: boolean) {
        this._canCopy = newValue;
        ReduxUpdater.ribbonRefresh();
    }

    static get canSessionPaste(): boolean {
        return this._canSessionPaste;
    }

    static set canSessionPaste(newValue: boolean) {
        this._canSessionPaste = newValue;
        ReduxUpdater.ribbonRefresh();
    }

    static get contentHistoryManager(): Core.ContentEditHistoryManager {
        return this._contentHistoryManager;
    }

    static set contentHistoryManager(newInstance: Core.ContentEditHistoryManager) {
        this._contentHistoryManager = newInstance;
    }

    static get doc(): Core.Document {
        return this._doc;
    }

    static set doc(doc: Core.Document) {
        this._doc = doc;
    }

    static get documentViewer(): Core.DocumentViewer {
        return this._documentViewer;
    }

    static set documentViewer(documentViewer: Core.DocumentViewer) {
        this._documentViewer = documentViewer;

        this.annotationHistoryManager = this._documentViewer.getAnnotationHistoryManager();
        this.contentHistoryManager = this._documentViewer.getContentEditHistoryManager();

        this.documentViewer.addEventListener('annotationsLoaded', () => {
            devLog('[event] annotationsLoaded');
            ApryseSDK._annotationsLoaded = true;
            CustomHistoryManager.savedTime = Date.now();
            CustomHistoryManager.savedIds = ApryseSDK.annotationManager
                .getAnnotationsList()
                .map(a => a.Id);
        });

        this._documentViewer.addEventListener(
            'toolModeUpdated',
            (newTool: Core.Tools.Tool, oldTool: Core.Tools.Tool) => {
                devLog('toolModeUpdated ' + oldTool.name + ' >> ' + newTool.name);

                ReduxUpdater.stateName = newTool.name ?? 'Unknown';

                if (
                    newTool instanceof ApryseSDK._ToolsClass.RubberStampCreateTool &&
                    newTool.name !== 'InsertImageTool'
                ) {
                    ApryseSDK.setStamp(ReduxUpdater.selectedStamp)
                        .then(() => {
                            this._createStampTool.showPreview();
                        })
                        .catch(err => devError(err));
                }

                if (
                    newTool instanceof ApryseSDK._ToolsClass.ArrowCreateTool ||
                    newTool instanceof ApryseSDK._ToolsClass.DistanceMeasurementCreateTool ||
                    newTool instanceof ApryseSDK._ToolsClass.PerimeterMeasurementCreateTool ||
                    newTool instanceof ApryseSDK._ToolsClass.AreaMeasurementCreateTool
                ) {
                    if (
                        newTool instanceof ApryseSDK._ToolsClass.ArrowCreateTool ||
                        newTool instanceof ApryseSDK._ToolsClass.DistanceMeasurementCreateTool
                    ) {
                        newTool.setDrawMode(
                            ApryseSDK._ToolsClass.LineCreateTool.DrawModes
                                .TWO_CLICKS_HOLD_TO_DRAW_HYBRID,
                        );
                    }
                }
            },
        );
    }

    static get instance(): WebViewerInstance {
        return this._instance;
    }

    static set instance(webViewerInstance: WebViewerInstance) {
        const { Annotations, Tools, PDFNet } = webViewerInstance.Core;
        this._AnnotationsClass = Annotations;
        this._annotationsShiftVector = new webViewerInstance.Core.Math.Point(0, 0);
        this._MathClass = webViewerInstance.Core.Math;
        this._ToolsClass = Tools;
        this._pdfNetClass = PDFNet;
        this._instance = webViewerInstance;
        this._instance.UI.setMaxZoomLevel(10.0);
        this._instance.UI.setMinZoomLevel(0.1);
        this._instance.UI.addEventListener('contextmenu', this._onViewerContextMenu);
        this._instance.UI.addEventListener('languageChanged', this._onViewerLanguageChanged);
        this._instance.UI.annotationPopup.update([]);
        this._instance.UI.contextMenuPopup.update([]);
        this._instance.UI.disableElements([...this.AnnotationModeElements, 'signatureModal']);
        this._instance.UI.disableReplyForAnnotations(annot => {
            devLog('UI.disableReplyForAnnotation', annot);
            return true;
        });

        // Disable/remove 'include annotations' check box.
        this._instance.UI.disableElements(['annotationsPrintOption']);

        // Prevent Apryse popup
        this._instance.UI.disableFeatures([
            this._instance.UI.Feature.Initials,
            this._instance.UI.Feature.RightClickAnnotationPopup,
            this._instance.UI.Feature.Search,
        ]);

        this._instance.UI.setColorPalette({
            toolNames: [ApryseSDK.ToolsENUM.CREATE_FREETEXT],
            colors: ['#000000'],
        });

        this._instance.UI.setAnnotationContentOverlayHandler(
            (annotation: Core.Annotations.Annotation) => {
                const div = document.createElement('div');
                div.appendChild(document.createTextNode(`${annotation.Author}`));
                return div;
            },
        );

        // Prevent auto-deletion by SDK
        this._instance.UI.hotkeys.on('delete', {
            keydown: (ev: KeyboardEvent) => {
                ev.stopPropagation();
                handleDeleteKey();
            },
        });

        this._instance.UI.hotkeys.off(this._instance.UI.hotkeys.Keys.CTRL_C);
        this._instance.UI.hotkeys.off(this._instance.UI.hotkeys.Keys.COMMAND_C);
        this._instance.UI.hotkeys.off(this._instance.UI.hotkeys.Keys.CTRL_V);
        this._instance.UI.hotkeys.off(this._instance.UI.hotkeys.Keys.COMMAND_V);
        this._ToolsClass.Tool.disableAutoSwitch();

        class InsertImageTool extends Tools.RubberStampCreateTool {
            private static toolInstance: Core.Tools.RubberStampCreateTool;
            private static imageData = {
                pageNumber: 0,
                location: [0, 0],
                base64EncodedData: '',
                size: [0, 0],
            };
            constructor(viewerInstance: Core.DocumentViewer) {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                super(viewerInstance, ApryseSDK.instance.Core.Annotations.StampAnnotation);
                InsertImageTool.toolInstance = this;
            }
            mouseLeftDown(e: MouseEvent): void {
                const info = ApryseSDK.documentViewer.getViewerCoordinatesFromMouseEvent(e);
                InsertImageTool.imageData.pageNumber = info.pageNumber;
                InsertImageTool.imageData.location = [info.x, info.y];
            }
            async mouseLeftUp() {
                InsertImageTool.toolInstance.hidePreview();
                const { pageNumber, location, base64EncodedData, size } = InsertImageTool.imageData;
                if (pageNumber && location && base64EncodedData && size) {
                    const annot = new Annotations.StampAnnotation({
                        PageNumber: pageNumber,
                        X: location[0] - size[0] / 2,
                        Y: location[1] - size[1] / 2,
                        Width: size[0],
                        Height: size[1],
                    });
                    annot.Author = ApryseSDK.annotationManager.getCurrentUser();
                    await annot.setImageData(base64EncodedData);
                    ApryseSDK.annotationManager.addAnnotation(annot);
                    ApryseSDK.annotationManager.redrawAnnotation(annot);
                    ApryseSDK.setToolMode(ApryseSDK.ToolsENUM.ANNOTATION_EDIT);
                }
            }
            async onFileInput(e: Event) {
                const inputEl = e.currentTarget as HTMLInputElement;
                const file = inputEl.files?.[0];
                if (file) {
                    const fileReader = new FileReader();
                    fileReader.readAsDataURL(file);
                    fileReader.onloadend = async () => {
                        const img = document.createElement('img');
                        img.onload = () => {
                            InsertImageTool.imageData.size = [img.naturalWidth, img.naturalHeight];
                            URL.revokeObjectURL(img.src);
                            img.remove();
                        };
                        img.src = URL.createObjectURL(file);
                        InsertImageTool.imageData.base64EncodedData = fileReader.result as string;
                        inputEl?.remove();
                        const annot = new Annotations.StampAnnotation();
                        annot.Author = ApryseSDK.annotationManager.getCurrentUser();
                        await annot.setImageData(fileReader.result as string);
                        InsertImageTool.toolInstance
                            .setRubberStamp(annot, '')
                            .then(() => InsertImageTool.toolInstance.showPreview());
                    };
                } else {
                    inputEl?.remove();
                }
            }
            onFileCancel(e: Event) {
                const inputEl = e.currentTarget as HTMLInputElement;
                inputEl?.remove();
                ApryseSDK.setToolMode(ApryseSDK.ToolsENUM.ANNOTATION_EDIT);
            }
            switchIn(_oldTool: Core.Tools.Tool): void {
                const input = document.createElement('input');
                input.setAttribute('hidden', 'true');
                input.setAttribute('type', 'file');
                input.setAttribute('accept', '.jpg,.jpeg,.png');
                input.addEventListener('change', this.onFileInput);
                input.addEventListener('cancel', this.onFileCancel);
                input.click();
            }
            switchOut(_newTool: Core.Tools.Tool): void {
                InsertImageTool.imageData.base64EncodedData = '';
                InsertImageTool.toolInstance.hidePreview();
            }
        }

        this._instance.UI.registerTool(
            {
                toolName: ApryseSDK.ToolsENUM.INSERT_IMAGE_TOOL,
                toolObject: new InsertImageTool(this._documentViewer),
                buttonImage: '',
                buttonName: 'insertImageToolButton',
                tooltip: 'InsertImage',
            },
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            InsertImageTool,
        );

        this.updateFreeTextFontStyles();

        const signatureTool = this._documentViewer.getTool(ApryseSDK.ToolsENUM.SIGNATURE);
        if (signatureTool) {
            signatureTool.addEventListener('signatureReady', signature => {
                signature.ReadOnly = true;
            });
        }

        const storedData = localStorage.getItem('apryse-textDialog-fontAttributes');
        if (storedData) {
            ReduxUpdater.selectedFontAttributes = JSON.parse(storedData);
            this.updateFreeTextFontStyles();
        }

        window.addEventListener('storage', event => {
            if (event.newValue && event.key === 'apryse-textDialog-fontAttributes') {
                ReduxUpdater.selectedFontAttributes = JSON.parse(event.newValue);
                this.updateFreeTextFontStyles();
            }
        });

        ReduxUpdater.ribbonRefresh();
    }

    static Point(x: number, y: number) {
        return new this._instance.Core.Math.Point(x, y);
    }

    static setLanguage(language: string) {
        this._instance?.UI.setLanguage(language);
    }

    static get MarqueeZoomTool(): Core.Tools.MarqueeZoomTool {
        const { Tools } = this._instance.Core;
        return new Tools.MarqueeZoomTool(this._documentViewer);
    }

    static get pageCanvases(): { [id: number]: HTMLCanvasElement } {
        return this._pageCanvases;
    }

    static set pageCanvases(canvases: { [id: number]: HTMLCanvasElement }) {
        this._pageCanvases = canvases;
    }

    /**
     * Returns the document's page count.
     */
    static get pageCount(): number {
        if (!this._documentLoaded || ReduxUpdater.viewMode === PdfViewerViewModeFlag.Error) {
            return 0;
        }
        return this._documentViewer?.getPageCount?.() || 0;
    }

    static get pageMatrix(): Core.PDFNet.Matrix2D {
        return this._doc.getPageMatrix(ApryseUpdater.currentPage);
    }

    static get pdfDoc(): Core.PDFNet.PDFDoc {
        return this._pdfDoc;
    }

    static set pdfDoc(pdfDoc: Core.PDFNet.PDFDoc) {
        this._pdfDoc = pdfDoc;
    }

    static get pdfNet(): typeof Core.PDFNet {
        return this._pdfNetClass;
    }

    static get permissionMapping(): [keyof IPermission, number][] {
        return [
            ['Print', this._pdfNetClass.SecurityHandler.Permission.e_print],
            ['ChangeDocument', this._pdfNetClass.SecurityHandler.Permission.e_doc_modify],
            ['StructureOfTheDocument', this._pdfNetClass.SecurityHandler.Permission.e_assemble_doc],
            ['CopyContent', this._pdfNetClass.SecurityHandler.Permission.e_extract_content],
            [
                'PossibilityToAccessContent',
                this._pdfNetClass.SecurityHandler.Permission.e_extract_content,
            ],
            ['ExtractPages', this._pdfNetClass.SecurityHandler.Permission.e_extract_content],
            ['CreateAnnotations', this._pdfNetClass.SecurityHandler.Permission.e_mod_annot],
            ['FillInFormFields', this._pdfNetClass.SecurityHandler.Permission.e_fill_forms],
            ['SignDigitally', -1],
            ['CreateNewPages', this._pdfNetClass.SecurityHandler.Permission.e_assemble_doc],
        ];
    }

    static get selectedAnnotation(): Core.Annotations.Annotation | null {
        return ApryseSDK.selectedAnnotations[0] ?? null;
    }

    static get selectedAnnotations(): Core.Annotations.Annotation[] {
        return this._annotationManager.getSelectedAnnotations();
    }

    static get signatureData(): { [id: number]: ISignatureData } {
        return this._signatureData;
    }

    static get SnapModes() {
        return this._ToolsClass.SnapModes;
    }

    static get textAnnotationCopyTextContext(): Core.Annotations.Annotation | null {
        return this._textAnnotationCopyTextContext;
    }

    static set textAnnotationCopyTextContext(newValue: Core.Annotations.Annotation | null) {
        this._textAnnotationCopyTextContext = newValue;
    }

    static get textAnnotationEditingContext(): Core.Annotations.Annotation | null {
        return this._textAnnotationEditingContext;
    }

    static set textAnnotationEditingContext(newValue: Core.Annotations.Annotation | null) {
        this._textAnnotationEditingContext = newValue;
        ApryseSDK.canCopy = newValue != null || ApryseSDK.selectedAnnotations.length > 0;
    }

    static get undoManager(): Core.PDFNet.UndoManager {
        return this._undoManager;
    }

    static set undoManager(newInstance: Core.PDFNet.UndoManager) {
        this._undoManager = newInstance;
    }

    /**
     * Global functions
     */
    static adjustAnnotations() {
        // Move all signatures to the front of the document
        const allAnnotations = this._annotationManager
            ? this._annotationManager.getAnnotationsList()
            : [];
        allAnnotations
            .filter(annot => annot.Author === 'EPLASS Schriftfeld')
            .forEach(annot => this._annotationManager.bringToFront(annot));
        allAnnotations
            .filter(annot => annot instanceof ApryseSDK.AnnotationsClass.FreeTextAnnotation)
            .forEach(annot => {
                if (annot.getIntent() === FreeTextIntents.Callout) {
                    if (GlobalConstants.forceDefaultFont) {
                        annot.Font = GlobalConstants.default.fontFamily;
                    }
                    annot.fitText(
                        ApryseSDK.doc.getPageInfo(annot.PageNumber),
                        ApryseSDK.doc.getPageMatrix(annot.PageNumber),
                        ApryseSDK.doc.getPageRotation(annot.PageNumber),
                    );
                }
            });
        allAnnotations
            .filter(annot => ['textbox', 'typewriter'].includes(ApryseSDK.annotPrettyType(annot)))
            .forEach(annot => annot.disableRotationControl());
    }

    static annotationsDeepCopy(annots: Core.Annotations.Annotation[], preserveIDs = false) {
        return annots.map(annot => this.annotDeepCopy(annot, preserveIDs));
    }

    static annotDeepCopy(annot: Core.Annotations.Annotation, preserveIDs = false) {
        return this._annotationManager.getAnnotationCopy(annot, {
            copyAssociatedLink: false,
            copyReplies: false,
            copyWithSameId: preserveIDs,
        });
    }

    static annotColor(annot: Core.Annotations.Annotation, newColor?: number): number {
        if (annot instanceof ApryseSDK.AnnotationsClass.MarkupAnnotation) {
            if (newColor !== undefined && canEditAnnot(annot)) {
                annot.Color = this.intToColor(newColor);
                this._annotationManager.redrawAnnotation(annot);
            }
            return intColor(annot.Color);
        }
        return -1;
    }

    /** Get or set for example "12pt", only for FreeTextAnnotation. */
    static annotFontSize(annot: Core.Annotations.Annotation, newFontSize?: string): string {
        if (annot instanceof ApryseSDK.AnnotationsClass.FreeTextAnnotation) {
            if (newFontSize && canEditAnnot(annot)) {
                annot.FontSize = newFontSize;
                this._annotationManager.redrawAnnotation(annot);
            }
            return annot.FontSize;
        }
        return '';
    }

    static annotEndStyle(
        annot: Core.Annotations.Annotation,
        newStyle?: Core.Annotations.LineEndType,
    ): Core.Annotations.LineEndType {
        if (annot instanceof ApryseSDK.AnnotationsClass.LineAnnotation) {
            if (newStyle && canEditAnnot(annot)) {
                annot.setEndStyle(newStyle);
                this._annotationManager.redrawAnnotation(annot);
            }
            return annot.getEndStyle();
        }
        if (annot instanceof ApryseSDK.AnnotationsClass.PolylineAnnotation) {
            if (newStyle && canEditAnnot(annot)) {
                annot.setEndStyle(newStyle);
                this._annotationManager.redrawAnnotation(annot);
            }
            return annot.getEndStyle();
        }
        return this._AnnotationsClass.LineEndType.NONE;
    }

    static annotFillColor(annot: Core.Annotations.Annotation, newColor?: number): number {
        if (annot instanceof ApryseSDK.AnnotationsClass.MarkupAnnotation) {
            if (newColor !== undefined && canEditAnnot(annot)) {
                annot.FillColor = this.intToColor(newColor);
                this._annotationManager.redrawAnnotation(annot);
            }
            return intColor(annot.FillColor);
        }
        return -1;
    }

    static annotIntent(annot: Core.Annotations.Annotation) {
        if (annot instanceof ApryseSDK.AnnotationsClass.FreeTextAnnotation) {
            switch (annot.getIntent()) {
                case FreeTextIntents.Callout:
                    return 'FreeTextCallout';
                case FreeTextIntents.Typewriter:
                    return 'FreeTextTypeWriter';
            }
        }
        if (annot instanceof ApryseSDK.AnnotationsClass.LineAnnotation && annot.Scale) {
            return 'LineDimension';
        }
        if (annot instanceof ApryseSDK.AnnotationsClass.PolylineAnnotation && annot.Scale) {
            return 'PolylineDimension';
        }
        if (annot instanceof ApryseSDK.AnnotationsClass.PolygonAnnotation) {
            switch (annot.Intent) {
                case 'PolygonCloud':
                    return 'PolygonCloud';
                case 'PolygonDimension':
                    return 'PolygonDimension';
            }
            if (annot.Scale) {
                return 'PolygonDimension';
            }
        }
        return '';
    }

    static annotMove(
        annot: Core.Annotations.Annotation,
        x: number,
        y: number,
        useShiftVFector = false,
    ) {
        const deepCopy = this.annotationManager.getAnnotationCopy(annot, {});
        const offsX = useShiftVFector ? this._annotationsShiftVector.x : 0.0;
        const offsY = useShiftVFector ? this._annotationsShiftVector.y : 0.0;
        deepCopy.move(ApryseSDK.rectMove(annot.getRect(), x - offsX, y - offsY));
        return deepCopy;
    }

    static annotOpacity(annot: Core.Annotations.Annotation, newOpacity?: number): number {
        if (annot instanceof ApryseSDK.AnnotationsClass.MarkupAnnotation) {
            if (newOpacity && canEditAnnot(annot)) {
                annot.Opacity = newOpacity;
                this._annotationManager.redrawAnnotation(annot);
            }
            return annot.Opacity;
        }
        return -1;
    }

    private static annotPrettyTypeCheckCommon(annot: Core.Annotations.Annotation): string {
        switch (annot.constructor) {
            case ApryseSDK.AnnotationsClass.TextHighlightAnnotation:
                return 'highlight';
            case ApryseSDK.AnnotationsClass.TextSquigglyAnnotation:
                return 'squiggly';
            case ApryseSDK.AnnotationsClass.TextStrikeoutAnnotation:
                return 'strikeout';
            case ApryseSDK.AnnotationsClass.TextUnderlineAnnotation:
                return 'underline';
            case ApryseSDK.AnnotationsClass.StickyAnnotation:
                return 'note';
            case ApryseSDK.AnnotationsClass.RectangleAnnotation:
                return 'square';
            case ApryseSDK.AnnotationsClass.EllipseAnnotation:
                return 'circle';
            case ApryseSDK.AnnotationsClass.FreeHandAnnotation:
                return 'pencil';
            case ApryseSDK.AnnotationsClass.StampAnnotation:
                return 'stamp';
            case ApryseSDK.AnnotationsClass.SignatureWidgetAnnotation:
                return 'signatureWidget';
        }
        return '';
    }

    private static annotPrettyTypeCheckForTextAnnotation(
        annot: Core.Annotations.Annotation,
    ): string {
        if (annot instanceof ApryseSDK.AnnotationsClass.FreeTextAnnotation) {
            switch (annot.getIntent()) {
                case FreeTextIntents.Callout:
                    return 'callout';
                case FreeTextIntents.Typewriter:
                    return 'typewriter';
            }
            return 'textbox';
        }
        return '';
    }

    static annotPrettyType(annot: Core.Annotations.Annotation): string {
        let result = this.annotPrettyTypeCheckForTextAnnotation(annot);
        if (result !== '') {
            return result;
        }

        result = this.annotPrettyTypeCheckCommon(annot);
        if (result !== '') {
            return result;
        }

        if (annot instanceof ApryseSDK.AnnotationsClass.LineAnnotation) {
            if (annot.Scale) {
                return 'distance';
            }
            return 'line';
        }
        if (annot instanceof ApryseSDK.AnnotationsClass.PolylineAnnotation) {
            if (annot.Scale) {
                return 'perimeter';
            }
            return 'polyline';
        }
        if (annot instanceof ApryseSDK.AnnotationsClass.PolygonAnnotation) {
            switch (annot.Intent) {
                case 'PolygonCloud':
                    return 'cloud';
                case 'PolygonDimension':
                    return 'area';
            }
            if (annot.Scale) {
                return 'area';
            }
            return 'polygon';
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const xsiType = (annot as any)['_xsi:type'];

        if (xsiType !== ApryseSDK.ToolsENUM.PASTE_PREVIEW) {
            devError(new Error(`Unknown annotation type '${xsiType}'!!!`));
        }

        return '!!!UNKNOWN!!!';
    }

    static annotRotation(annot: Core.Annotations.Annotation, rotateDegrees?: number): number {
        if (['textbox', 'typewriter'].includes(ApryseSDK.annotPrettyType(annot))) {
            // Disable rotations for textbox and typewriter
            return annot.Rotation;
        }

        if (rotateDegrees && canEditAnnot(annot)) {
            if (GlobalVariables.rotationAngles[annot.Id] === undefined) {
                GlobalVariables.rotationAngles[annot.Id] = this.normaliseDegrees(-annot.Rotation);
            }
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore ToDO: remove when SDK lib is documanted
            annot.rotate(
                (Math.PI * (rotateDegrees - GlobalVariables.rotationAngles[annot.Id])) / 180.0,
            );
            this._annotationManager.redrawAnnotation(annot);
            GlobalVariables.rotationAngles[annot.Id] = rotateDegrees;
        }
        return annot.Rotation;
    }

    static annotStartStyle(
        annot: Core.Annotations.Annotation,
        newStyle?: Core.Annotations.LineEndType,
    ): Core.Annotations.LineEndType {
        if (annot instanceof ApryseSDK.AnnotationsClass.LineAnnotation) {
            if (newStyle && canEditAnnot(annot)) {
                annot.setStartStyle(newStyle);
                this._annotationManager.redrawAnnotation(annot);
            }
            return annot.getStartStyle();
        }
        if (annot instanceof ApryseSDK.AnnotationsClass.PolylineAnnotation) {
            if (newStyle && canEditAnnot(annot)) {
                annot.setStartStyle(newStyle);
                this._annotationManager.redrawAnnotation(annot);
            }
            return annot.getStartStyle();
        }
        return this._AnnotationsClass.LineEndType.NONE;
    }

    static annotDashes(annot: Core.Annotations.Annotation, newDashes?: string): string {
        if (
            annot instanceof ApryseSDK.AnnotationsClass.EllipseAnnotation ||
            annot instanceof ApryseSDK.AnnotationsClass.LineAnnotation ||
            annot instanceof ApryseSDK.AnnotationsClass.PolylineAnnotation ||
            annot instanceof ApryseSDK.AnnotationsClass.PolygonAnnotation
        ) {
            if (newDashes) {
                annot.Dashes = newDashes;
            }
            const dashes = annot.Dashes;
            if (Array.isArray(dashes)) {
                return dashes.join(',');
            }
            return dashes;
        }

        return '';
    }

    static annotStyle(annot: Core.Annotations.Annotation, newStyle?: string): string {
        if (
            annot instanceof ApryseSDK.AnnotationsClass.EllipseAnnotation ||
            annot instanceof ApryseSDK.AnnotationsClass.LineAnnotation ||
            annot instanceof ApryseSDK.AnnotationsClass.PolylineAnnotation ||
            annot instanceof ApryseSDK.AnnotationsClass.PolygonAnnotation
        ) {
            if (newStyle) {
                annot.Style = newStyle;
            }
            return annot.Style;
        }

        return '';
    }

    static annotIntensity(annot: Core.Annotations.Annotation, newIntesity?: number): number {
        if (
            annot instanceof ApryseSDK.AnnotationsClass.PolygonAnnotation ||
            annot instanceof ApryseSDK.AnnotationsClass.RectangleAnnotation
        ) {
            if (newIntesity) {
                annot.Intensity = newIntesity;
            }
            return annot.Intensity;
        }

        return 0;
    }

    static applyStyling(annot: Core.Annotations.Annotation, newStyling?: string): string {
        let style = this.annotStyle(annot);
        let dashes = this.annotDashes(annot);
        let intensity = this.annotIntensity(annot);

        if (newStyling) {
            switch (newStyling) {
                case 'dashed small':
                    style = 'dash';
                    dashes = '2,2,2,2';
                    break;
                case 'dashed medium':
                    style = 'dash';
                    dashes = '3,3,3,3';
                    break;
                case 'dashed large':
                    style = 'dash';
                    dashes = '4,4,4,4';
                    break;
                case 'dashed large small':
                    style = 'dash';
                    dashes = '4,3,2,3';
                    break;
                case 'dashed large xl':
                    style = 'dash';
                    dashes = '8,4,4,4';
                    break;
                case 'dashed xl medium':
                    style = 'dash';
                    dashes = '4,3,16,3';
                    break;
                case 'cloudy small':
                    style = 'cloudy';
                    intensity = 1;
                    break;
                case 'cloudy large':
                    style = 'cloudy';
                    intensity = 2;
                    break;
            }

            style = this.annotStyle(annot, style);
            dashes = this.annotDashes(annot, dashes);
            intensity = this.annotIntensity(annot, intensity);

            this._annotationManager.redrawAnnotation(annot);
        }

        return `${style}|${dashes}|${intensity}`;
    }

    static annotStrokeColor(annot: Core.Annotations.Annotation, newColor?: number): number {
        if (annot instanceof ApryseSDK.AnnotationsClass.MarkupAnnotation) {
            if (newColor !== undefined && canEditAnnot(annot)) {
                annot.StrokeColor = this.intToColor(newColor);
                this._annotationManager.redrawAnnotation(annot);
            }
            return intColor(annot.StrokeColor);
        }
        return -1;
    }

    static annotTextColor(annot: Core.Annotations.Annotation, newColor?: number): number {
        if (annot instanceof ApryseSDK.AnnotationsClass.FreeTextAnnotation) {
            if (newColor !== undefined && canEditAnnot(annot)) {
                annot.TextColor = this.intToColor(newColor);
                this._annotationManager.redrawAnnotation(annot);
            }
            return intColor(annot.TextColor);
        }
        return -1;
    }

    static annotThickness(annot: Core.Annotations.Annotation, newThickness?: number): number {
        if (annot instanceof ApryseSDK.AnnotationsClass.MarkupAnnotation) {
            if (newThickness && canEditAnnot(annot)) {
                annot.StrokeThickness = newThickness;
                this._annotationManager.redrawAnnotation(annot);
            }
            return annot.StrokeThickness;
        }
        return -1;
    }

    /**
     * @param r - red (0-255)
     * @param g - green (0-255)
     * @param b - blue (0-255)
     * @param a - alpha opacity (0-1)
     */
    static Color(r: number, g: number, b: number, a?: number) {
        return new this._AnnotationsClass.Color(r, g, b, a);
    }

    static commonCopy() {
        ApryseSDK.textAnnotationEditingContext ? ApryseSDK.copyText() : ApryseSDK.copyAnnotations();
    }

    static commonPaste() {
        ApryseSDK.textAnnotationEditingContext
            ? ApryseSDK.pasteText()
            : ApryseSDK.pasteSessionAnnotations();
    }

    static commonCut() {
        if (ApryseSDK.textAnnotationEditingContext) {
            ApryseSDK.cutText();
        }
    }

    static commonSelectAll() {
        ApryseSDK.textAnnotationEditingContext
            ? ApryseSDK.selectAllText()
            : ApryseSDK.selectAllAnnotations();
    }

    static copyAnnotations() {
        try {
            ApryseSDK.deselectForeignAnnotations();

            const copyAnnots = ApryseSDK.selectedAnnotations ?? [];

            if (copyAnnots.length) {
                ApryseSDK.annotationManager
                    .exportAnnotations({
                        annotList: copyAnnots,
                        widgets: false,
                        links: false,
                    })
                    .then(annotData => {
                        ApryseSDK.broadcastMessage = annotData;
                        this._broadcastChannel.postMessage(annotData);
                    });
                GlobalDialogs.showNoty(
                    i18next.t(
                        copyAnnots.length > 1
                            ? 'notifications.annotationsCopied'
                            : 'notifications.annotationCopied',
                        {
                            count: copyAnnots.length,
                        },
                    ),
                    ToastSeverity.Success,
                );
            }
        } catch (e) {
            GlobalDialogs.showError((e as Error)?.message);
        }
    }

    static copyMeasurementInfo(): void {
        const annotation = ApryseSDK.selectedAnnotation;
        if (!annotation) {
            return;
        }

        if (
            annotation instanceof ApryseSDK.AnnotationsClass.LineAnnotation ||
            annotation instanceof ApryseSDK.AnnotationsClass.PolylineAnnotation ||
            annotation instanceof ApryseSDK.AnnotationsClass.PolygonAnnotation
        ) {
            if (!annotation.Scale) {
                return;
            }

            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore ToDO: remove when SDK lib is documanted
            const displayValue = annotation.getMeasurementTextWithScaleAndUnits();
            writeToClipboard(displayValue);
        }
    }

    static copyText() {
        let freeText =
            ApryseSDK.textAnnotationCopyTextContext as Core.Annotations.FreeTextAnnotation;
        if (!freeText) {
            freeText =
                ApryseSDK.textAnnotationEditingContext as Core.Annotations.FreeTextAnnotation;
        }

        if (freeText) {
            const editor = freeText.getEditor();

            if (!editor) {
                GlobalDialogs.showNoty(
                    i18next.t('notifications.nothingCopiedToClipboard'),
                    ToastSeverity.Error,
                );
                return;
            }

            let selectedText = editor.getContents();
            const cursorSelection = editor.getSelection();
            if (cursorSelection) {
                selectedText = editor
                    .getContents()
                    .substring(
                        cursorSelection.index,
                        cursorSelection.index + cursorSelection.length,
                    );
            }

            writeToClipboard(selectedText);
            GlobalDialogs.showNoty(
                i18next.t('notifications.textCopiedToClipboard'),
                ToastSeverity.Success,
            );
        }
    }

    static cutText() {
        ApryseSDK.copyText();

        const freeText =
            ApryseSDK.textAnnotationEditingContext as Core.Annotations.FreeTextAnnotation;

        if (freeText) {
            const editor = freeText.getEditor();
            const cursorSelection = editor.getSelection();
            const allText = editor.getContents();

            const begin = allText.substring(0, cursorSelection.index);
            const end = allText.substring(
                cursorSelection.index + cursorSelection.length,
                allText.length,
            );

            editor.setText(begin + end);
        }
    }

    private static decodeAlignment(alignmentArray: boolean[]) {
        if (!alignmentArray || alignmentArray.length === 0) {
            return 'left';
        }

        if (alignmentArray[0]) {
            return 'left';
        }
        if (alignmentArray[1]) {
            return 'center';
        }
        if (alignmentArray[2]) {
            return 'right';
        }
        return 'justify';
    }

    static deleteAnnotations(annotations: Core.Annotations.Annotation[]): void {
        if (!annotations?.length) {
            return;
        }

        const filteredAnnotations = annotations.filter(annot => canEditAnnot(annot));
        if (filteredAnnotations.length === 0) {
            return;
        }

        ApryseSDK.annotationManager.deleteAnnotations(filteredAnnotations, { force: true });
    }

    static deleteSelectedAnnotations(): void {
        this.deleteAnnotations(ApryseUpdater.selectedAnnotations);
    }

    static deselectForeignAnnotations(): void {
        const before = ApryseSDK.selectedAnnotations.length;
        this.annotationManager.deselectAnnotations(
            ApryseUpdater.selectedAnnotations.filter(annot => !canEditAnnot(annot)),
        );
        if (ApryseSDK.selectedAnnotations.length < before) {
            GlobalDialogs.showNoty(
                i18next.t('notifications.annotationDeselected'),
                ToastSeverity.Info,
            );
        }
    }

    static disableApryseUiElements(): void {
        if (!this._instance) {
            return;
        }
        this._instance.UI.disableElements(this.AnnotationModeElements);
    }

    static async embeddedFiles(): Promise<EmbeddedFileAttachmentMeta[]> {
        const { PDFNet } = this._instance.Core;
        const files = await PDFNet.NameTree.find(ApryseSDK.pdfDoc, 'EmbeddedFiles');
        const embeddedAttachments: EmbeddedFileAttachmentMeta[] = [];

        if (files && (await files.isValid())) {
            const i = await files.getIteratorBegin();
            for (let cnt = 0; await i.hasNext(); await i.next(), ++cnt) {
                const fileName = await i.key().then(key => key.getAsPDFText());
                const fileSpec = await PDFNet.FileSpec.createFromObj(await i.value());
                const fileSize = await (await fileSpec.getFileData()).size();

                embeddedAttachments.push({
                    fileSpec,
                    fileName,
                    fileSize,
                } as EmbeddedFileAttachmentMeta);
            }
        }

        return embeddedAttachments;
    }

    static enableApryseUiElements(): void {
        if (!this._instance) {
            return;
        }
        this._instance.UI.enableElements(this.AnnotationModeElements);
    }

    static getCreationTime(annot: Core.Annotations.Annotation): Date {
        if (annot instanceof ApryseSDK.AnnotationsClass.MarkupAnnotation) {
            return annot.DateCreated;
        }

        devWarning(
            `Could not get "created date" timestamp from annotation ${annot.Id}, because its not type of "Markup".`,
        );

        return annot.DateModified ?? new Date();
    }

    /**
     * Export all annotations on document as XFDF (XML).
     */
    static async getAnnotationsXFDF(): Promise<string> {
        return await this._annotationManager.exportAnnotations();
    }

    /**
     * Export all annotations on document as FDF (binary).
     */
    static async getAnnotationsFDF(): Promise<Blob> {
        const xfdf = await this._annotationManager.exportAnnotations();
        const fdfDoc = await this._instance.Core.PDFNet.FDFDoc.createFromXFDF(xfdf);
        const uInt8Array = await fdfDoc.saveMemoryBuffer();

        return new Blob([uInt8Array] as BlobPart[]);
    }

    static async getDocumentData(): Promise<Blob> {
        const doc = this._documentViewer.getDocument();
        const xfdfString = await ApryseSDK.getAnnotationsXFDF();
        const data = await doc.getFileData({ xfdfString }); // saves the document with annotations in it
        const arr = new Uint8Array(data);
        const blob = new Blob([arr], { type: 'application/pdf' });

        return blob;
    }

    private static getDocumentFromUrl = async (url: string): Promise<Core.PDFNet.PDFDoc> => {
        const newDoc = await this._instance.Core.createDocument(
            url.startsWith('http') ? url : `${window.location.origin}/${url}`,
        );
        return await newDoc.getPDFDoc();
    };

    static readonly getEmbeddedFonts = async (): Promise<Core.PDFNet.Font[]> => {
        const embeddedFonts: Core.PDFNet.Font[] = [];

        const pdfDoc: Core.PDFNet.PDFDoc = await this._documentViewer.getDocument().getPDFDoc();

        const sdfdoc = await pdfDoc.getSDFDoc();
        const xRefSize = await sdfdoc.xRefSize();

        for (let i = 0; i < xRefSize; ++i) {
            const obj = await sdfdoc.getObj(i);

            if (!(await this.objectIsFont(obj))) {
                continue;
            }

            const font = await this._pdfNetClass.Font.createFromObj(obj);

            if (await font.isEmbedded()) {
                embeddedFonts.push(font);
            }
        }

        return embeddedFonts;
    };

    static async getFontEncoding(_font: Core.PDFNet.Font, _fontType: number): Promise<string> {
        const defaultValue = 'Default';
        return defaultValue;
    }

    static async getFontInfo(font: Core.PDFNet.Font): Promise<IFontItem> {
        const fontType = await font.getType();
        const actualFont = await font.getName();
        //const encoding = await this.getFontEncoding(font, fontType);
        //const actualFontType = '';

        return {
            Type: fontType.toString(),
            Encoding: '', //encoding,
            ActualFont: actualFont.includes('+') ? actualFont.split('+')[1] : actualFont,
            ActualFontType: '', //actualFontType,
        };
    }

    static readonly getObjectType = async (indirectObj: Core.PDFNet.Obj): Promise<string> => {
        const typeObj = await indirectObj.findObj('Type');

        if (!typeObj || !(await typeObj.isName())) {
            return '';
        }

        return await typeObj.getName();
    };

    private static getPageArray = async (doc: Core.PDFNet.PDFDoc): Promise<Core.PDFNet.Page[]> => {
        const arr: Core.PDFNet.Page[] = [];
        const itr = await doc.getPageIterator(1);

        for (itr; await itr.hasNext(); itr.next()) {
            const page = await itr.current();
            arr.push(page);
        }

        return arr;
    };

    static readonly generateDocumentDiff = async (
        documentUrl1: string,
        documentUrl2: string,
    ): Promise<Core.PDFNet.PDFDoc> => {
        const [doc1, doc2] = await Promise.all([
            ApryseSDK.getDocumentFromUrl(documentUrl1),
            ApryseSDK.getDocumentFromUrl(documentUrl2),
        ]);

        this.removeAllDigitalSignatures(doc1);
        this.removeAllDigitalSignatures(doc2);

        const [doc1Pages, doc2Pages] = await Promise.all([
            ApryseSDK.getPageArray(doc1),
            ApryseSDK.getPageArray(doc2),
        ]);

        const newDoc = await this._instance.Core.PDFNet.PDFDoc.create();
        newDoc.lock();

        // we'll loop over the doc with the most pages
        const biggestLength = Math.max(doc1Pages.length, doc2Pages.length);
        const diffOptions = new this._instance.Core.PDFNet.PDFDoc.DiffOptions();
        diffOptions.setColorB(toCompareColor(ReduxUpdater.compareColorAdded));
        diffOptions.setColorA(toCompareColor(ReduxUpdater.compareColorRemoved));
        if (ReduxUpdater.compareMode !== null) {
            diffOptions.setBlendMode(ReduxUpdater.compareMode);
        }

        for (let i = 0; i < biggestLength; i++) {
            let page1 = doc1Pages[i];
            let page2 = doc2Pages[i];

            // handle the case where one document has more pages than the other
            if (!page1) {
                page1 = await doc1.pageCreate(); // create a blank page
            }
            if (!page2) {
                page2 = await doc2.pageCreate(); // create a blank page
            }
            // Remove all annotations
            const numAnnots1 = await page1.getNumAnnots();
            const numAnnots2 = await page2.getNumAnnots();
            for (let i = numAnnots1 - 1; i >= 0; --i) {
                await page1.annotRemoveByIndex(i);
            }
            for (let i = numAnnots2 - 1; i >= 0; --i) {
                await page2.annotRemoveByIndex(i);
            }
            await newDoc.appendVisualDiff(page1, page2, diffOptions);
        }

        newDoc.unlock();
        return newDoc;
    };

    static handleAnnotationChanged(
        annotations: Core.Annotations.Annotation[],
        action: 'add' | 'modify' | 'delete',
        customChangeInfo?: ISpecificChangeInfo,
        snapshotBefore?: Core.Annotations.Annotation[],
        snapshotAfter?: Core.Annotations.Annotation[],
    ) {
        devLog(
            'handleAnnotationChanged[' + ReduxUpdater.stateName + ']',
            action,
            customChangeInfo ?? '(no info)',
        );

        if (action === 'add') {
            annotations.forEach(annotation => {
                if (['textbox', 'typewriter'].includes(ApryseSDK.annotPrettyType(annotation))) {
                    annotation.disableRotationControl();
                }
            });
        }

        if (ReduxUpdater.stateName === ApryseSDK.ToolsENUM.PASTE_PREVIEW) {
            annotations.forEach(annot => {
                annot.X -= ApryseSDK.annotationsShiftVector.x;
                annot.Y -= ApryseSDK.annotationsShiftVector.y;
                ApryseSDK._annotationManager.redrawAnnotation(annot);
            });
        }

        // WORKAROUND: force callout to fit content, after being changed
        annotations
            .filter(annotation => ApryseSDK.annotPrettyType(annotation) === 'callout')
            .forEach(annot => {
                (annot as Core.Annotations.FreeTextAnnotation).fitText(
                    ApryseSDK.doc.getPageInfo(annot.PageNumber),
                    ApryseSDK.doc.getPageMatrix(annot.PageNumber),
                    ApryseSDK.doc.getPageRotation(annot.PageNumber),
                );
            });

        if (CustomHistoryManager.undoRedoInProgress) {
            devLog('Skipped "handleAnnotationChanged" handler. (Reason: ongoing undo/redo action)');
            return;
        }

        if (snapshotBefore && !snapshotAfter) {
            snapshotAfter = CustomHistoryManager.takeAnnotationsSnapshot();
        }

        CustomHistoryManager.appendHistoryAction({
            action,
            annotations,
            specificInfo: customChangeInfo,
            snapshotBefore,
            snapshotAfter,
        });

        RedliningActionLogger.appendLog(
            customChangeInfo ? `${action} ${customChangeInfo}` : action,
            annotations,
        );

        ReduxUpdater.documentSaved = CustomHistoryManager.areAllAnnotationsSaved();

        ReduxUpdater.undoRedoRefresh();
        ReduxUpdater.annotationSidepanelRefresh();
        ReduxUpdater.ribbonRefresh();
    }

    static intToColor(color: number, blackAsTransparent = true): Core.Annotations.Color {
        if (blackAsTransparent && color === 0) {
            return new this._AnnotationsClass.Color(255, 255, 255, 0);
        }

        // Get the color's RBG values
        // const a = (color & 0xff000000) >> 24;
        const r = (color & 0xff0000) >> 16;
        const g = (color & 0xff00) >> 8;
        const b = color & 0xff;

        return new this._AnnotationsClass.Color(r, g, b);
    }

    static isAnnotationSelected(annotation: Core.Annotations.Annotation): boolean {
        return this._annotationManager.getSelectedAnnotations().some(a => a.Id === annotation.Id);
    }

    static isFreeTextAnnotation(
        annot: Core.Annotations.Annotation,
    ): 'typewriter' | 'callout' | 'textbox' | null {
        if (annot instanceof ApryseSDK.AnnotationsClass.FreeTextAnnotation) {
            switch (annot.getIntent()) {
                case FreeTextIntents.Callout:
                    return 'callout';
                case FreeTextIntents.Typewriter:
                    return 'typewriter';
                default:
                    return 'textbox';
            }
        }
        return null;
    }

    static isFreeTextTool(tool: Core.Tools.Tool): boolean {
        const toolName = tool?.name ?? '';
        return toolName.includes('FreeText') || toolName.includes('Callout'); // Apryse uses this check internally
    }

    static isMarkup(annot: Core.Annotations.Annotation): boolean {
        if (annot instanceof ApryseSDK.AnnotationsClass.MarkupAnnotation) {
            return true;
        }
        return false;
    }

    static isMeasurement(annotation: Core.Annotations.Annotation): boolean {
        return ['distance', 'perimeter', 'area'].includes(this.annotPrettyType(annotation));
    }

    static lineEndingStyleToString(style: Core.Annotations.LineEndType): string {
        switch (style) {
            case this._AnnotationsClass.LineEndType.BUTT:
                return 'line';
            case this._AnnotationsClass.LineEndType.CIRCLE:
                return 'circle';
            case this._AnnotationsClass.LineEndType.CLOSED_ARROW:
                return 'closedArrow';
            case this._AnnotationsClass.LineEndType.DIAMOND:
                return 'diamond';
            case this._AnnotationsClass.LineEndType.OPEN_ARROW:
                return 'openArrow';
            case this._AnnotationsClass.LineEndType.R_CLOSED_ARROW:
                return 'reverseClosedArrow';
            case this._AnnotationsClass.LineEndType.R_OPEN_ARROW:
                return 'reverseOpenArrow';
            case this._AnnotationsClass.LineEndType.SLASH:
                return 'slash';
            case this._AnnotationsClass.LineEndType.SQUARE:
                return 'square';
            default:
                return 'none';
        }
    }

    static lineStyleToDashes(style: string): string {
        switch (style) {
            case 'dashed small':
                return '2,2,2,2';
            case 'dashed medium':
                return '3,3,3,3';
            case 'dashed large':
                return '4,4,4,4';
            case 'dashed large small':
                return '4,3,2,3';
            case 'dashed large xl':
                return '8,4,4,4';
            case 'dashed xl medium':
                return '4,3,16,3';
        }
        return '';
    }

    static lineStyleToIntensity(style: string): number {
        switch (style) {
            case 'cloudy small':
                return 1;
            case 'cloudy large':
                return 2;
        }
        return 0;
    }

    static lineStyleToStyle(style: string, withDashes = false): string {
        switch (style) {
            case 'dashed small':
            case 'dashed medium':
            case 'dashed large':
            case 'dashed large small':
            case 'dashed large xl':
            case 'dashed xl medium':
                return `dash${withDashes ? ',' + this.lineStyleToDashes(style) : ''}`;
            case 'cloudy small':
            case 'cloudy large':
                return 'cloudy';
        }
        return 'solid';
    }

    static async loadThumbnail(pageNumber: number): Promise<string> {
        return new Promise<string>((resolve, reject) => {
            const doc = this._documentViewer.getDocument();
            doc.loadThumbnail(pageNumber, thumbnail => {
                if (!thumbnail) {
                    reject(new Error(`Error generating thumbnail for page ${pageNumber}`));
                    return;
                }
                if (thumbnail.nodeName.toLowerCase() === 'canvas') {
                    resolve((thumbnail as HTMLCanvasElement).toDataURL());
                } else {
                    resolve((thumbnail as HTMLImageElement).src);
                }
            });
        });
    }

    // Marks the the given annotation as active selected.
    static markAnnotAsSelected(annot: Core.Annotations.Annotation, deselectAll = true) {
        if (deselectAll) {
            ApryseSDK.annotationManager.deselectAllAnnotations();
        }
        ApryseSDK.annotationManager.selectAnnotations([annot]);
    }

    static normaliseDegrees = (rotation: number) => {
        while (rotation < -180) {
            rotation += 360;
        }
        while (rotation > 180) {
            rotation -= 360;
        }
        return rotation;
    };

    static readonly objectIsFont = async (indirectObj: Core.PDFNet.Obj): Promise<boolean> => {
        if (await indirectObj.isFree()) {
            return false;
        }

        if (!(await indirectObj.isDict()) && !(await indirectObj.isStream())) {
            return false;
        }

        const typeObj = await indirectObj.findObj('Type');
        if (!typeObj || !(await typeObj.isName())) {
            return false;
        }

        if ((await typeObj.getName()) !== 'Font') {
            return false;
        }

        const subtypeObj = await indirectObj.findObj('Subtype');
        if (!!subtypeObj && (await subtypeObj.isName())) {
            const subtypeName = await subtypeObj.getName();
            if (subtypeName === 'CIDFontType0') {
                return false;
            }
        }

        return true;
    };

    static onAnnotationChanged(
        annotations: Core.Annotations.Annotation[],
        action: 'add' | 'modify' | 'delete',
        specificInfo?: Core.AnnotationManager.AnnotationChangedInfoObject,
        snapshotBefore?: Core.Annotations.Annotation[],
        snapshotAfter?: Core.Annotations.Annotation[],
    ) {
        if (!ApryseSDK._annotationsLoaded || CustomHistoryManager.undoRedoInProgress) {
            devLog('onAnnotationChanged [ignored]');
            return;
        }

        devLog(`onAnnotationChanged [${action}]`, annotations);

        if (action === 'add') {
            annotations.forEach(annotation => {
                if (['textbox', 'typewriter'].includes(ApryseSDK.annotPrettyType(annotation))) {
                    annotation.disableRotationControl();
                }
            });
        }

        if (ReduxUpdater.stateName === ApryseSDK.ToolsENUM.PASTE_PREVIEW) {
            annotations.forEach(annot => {
                annot.X -= ApryseSDK.annotationsShiftVector.x;
                annot.Y -= ApryseSDK.annotationsShiftVector.y;
                ApryseSDK._annotationManager.redrawAnnotation(annot);
            });
        }

        /**
         * WORKAROUND A:
         * Force callout to fit content, after being changed.
         *
         * WORKAROUND B:
         * Support-Ticket 251204-5964-05
         * Set callout fontsize, after changing fontsize via Apryse popup.
         */

        const callouts = annotations.filter(
            annotation => ApryseSDK.annotPrettyType(annotation) === 'callout',
        ) as Core.Annotations.FreeTextAnnotation[];

        if (callouts.length > 0) {
            callouts.forEach(callout => {
                let highestFontSize = 0;
                const richtextStyles = callout.getRichTextStyle();
                Object.keys(richtextStyles).forEach(objKey => {
                    const fontSize = richtextStyles[objKey]['font-size']; // e.g. "12pt"

                    if (fontSize && Number(fontSize.replace('pt', '')) > highestFontSize) {
                        highestFontSize = Number(fontSize.replace('pt', ''));
                    }
                });

                if (highestFontSize) {
                    ApryseSDK.annotFontSize(callout, `${highestFontSize}pt`); // WORKAROUND B
                }

                callout.fitText(
                    ApryseSDK.doc.getPageInfo(callout.PageNumber),
                    ApryseSDK.doc.getPageMatrix(callout.PageNumber),
                    ApryseSDK.doc.getPageRotation(callout.PageNumber),
                ); // WORKAROUND A
            });
        }

        if (CustomHistoryManager.undoRedoInProgress) {
            devLog('Skipped "onAnnotationChanged" handler. (Reason: ongoing undo/redo action)');
            return;
        }

        if (!snapshotBefore) {
            snapshotBefore = CustomHistoryManager.getPrevHistoryAction()?.snapshotAfter;
        }

        if (!snapshotAfter) {
            snapshotAfter = CustomHistoryManager.takeAnnotationsSnapshot();
        }

        CustomHistoryManager.appendHistoryAction({
            action,
            annotations,
            specificInfo: 'unknown',
            snapshotBefore,
            snapshotAfter,
        });

        RedliningActionLogger.appendLog(action, annotations);

        ReduxUpdater.documentSaved = CustomHistoryManager.areAllAnnotationsSaved();

        ReduxUpdater.undoRedoRefresh();
        ReduxUpdater.annotationSidepanelRefresh();
        ReduxUpdater.ribbonRefresh();
    }

    static onAnnotationCreated(annotation: Core.Annotations.Annotation) {
        if (ApryseSDK.isFreeTextAnnotation(annotation)) {
            ApryseSDK.textAnnotationEditingContext = annotation;

            if (ApryseSDK.annotPrettyType(annotation) === 'typewriter') {
                setTimeout(() => {
                    (annotation as Core.Annotations.FreeTextAnnotation).setIntent(
                        FreeTextIntents.Typewriter,
                    );
                    ReduxUpdater.annotationSidepanelRefresh(); // refresh sidepanel after setIntent()
                }, 100);
            }
        }
    }

    static onAnnotationDoubleClick(annotation: Core.Annotations.Annotation) {
        if (ApryseSDK.isFreeTextAnnotation(annotation)) {
            ApryseSDK.textAnnotationEditingContext = annotation;
        }
    }

    static onAnnotationHistoryChanged() {
        ReduxUpdater.documentSaved = CustomHistoryManager.areAllAnnotationsSaved();
    }

    static onAnnotationSelected(
        annotations: Core.Annotations.Annotation[],
        action: 'selected' | 'deselected',
    ) {
        ApryseSDK.textAnnotationEditingContext = null;
        ApryseUpdater.selectedAnnotations = ApryseSDK.annotationManager.getSelectedAnnotations();
        ApryseSDK.canCopy = ApryseUpdater.selectedAnnotations.length > 0;

        switch (action) {
            case 'selected':
                devLog('onAnnotationSelected', annotations);
                break;
            case 'deselected':
                devLog('onAnnotationDeselected', annotations);
                break;
        }

        const isFreeText =
            ApryseSDK.selectedAnnotation &&
            ApryseSDK.isFreeTextAnnotation(ApryseSDK.selectedAnnotation);

        if (
            ApryseSDK.selectedAnnotation &&
            ['textbox', 'typewriter'].includes(
                ApryseSDK.annotPrettyType(ApryseSDK.selectedAnnotation),
            )
        ) {
            ApryseSDK.selectedAnnotation.disableRotationControl();
        }

        if (
            ReduxUpdater.useWorkflowColor &&
            (isFreeText || ApryseSDK.isFreeTextTool(ApryseSDK.documentViewer.getToolMode()))
        ) {
            ApryseSDK.instance.UI.disableElements(['colorPalette']);
        } else {
            ApryseSDK.instance.UI.enableElements(['colorPalette']);
        }
    }

    static async pasteSessionAnnotations() {
        if (!ApryseSDK.broadcastMessage) return;

        const { xfdfDoc, xfdfAnnots } = parseXFDFDoc(ApryseSDK.broadcastMessage);

        const currentPage = ApryseSDK.documentViewer.getCurrentPage();
        const pageHeight = ApryseSDK.documentViewer.getPageHeight(currentPage);
        const pasteOffset = { x: 0, y: 0 };

        xfdfAnnots.forEach(xfdfAnnot => {
            const originalRect = xfdfAnnot.getRect();
            const originalTopLeft = { x: originalRect[0], y: originalRect[3] };
            const annotWidth = roundDecimals(originalRect[2] - originalRect[0], 2);
            const annotHeight = roundDecimals(originalRect[3] - originalRect[1], 2);

            const newRect = [
                0 + pasteOffset.x,
                pageHeight - annotHeight - pasteOffset.y,
                annotWidth + pasteOffset.x,
                pageHeight - pasteOffset.y,
            ];
            const newTopLeft = { x: newRect[0], y: newRect[3] };

            xfdfAnnot.setRect(newRect);

            const vector = getVector(originalTopLeft, newTopLeft);

            if (xfdfAnnot.isCallout()) {
                const arrowCoords = xfdfAnnot.getCalloutArrowCoords();

                arrowCoords &&
                    xfdfAnnot.setCalloutArrowCoords({
                        start: {
                            x: arrowCoords.start.x + vector.x,
                            y: arrowCoords.start.y + vector.y,
                        },
                        knee: arrowCoords.knee
                            ? {
                                  x: arrowCoords.knee.x + vector.x,
                                  y: arrowCoords.knee.y + vector.y,
                              }
                            : null,
                        end: {
                            x: arrowCoords.end.x + vector.x,
                            y: arrowCoords.end.y + vector.y,
                        },
                    });
            }

            if (xfdfAnnot.isPolyline()) {
                const polyCoords = xfdfAnnot.getPolyCoords();

                polyCoords &&
                    polyCoords.forEach(polyCoord => {
                        polyCoord.x = polyCoord.x + vector.x;
                        polyCoord.y = polyCoord.y + vector.y;
                    });
                polyCoords && xfdfAnnot.setPolyCoords(polyCoords);
            }

            if (ReduxUpdater.useWorkflowColor) {
                const workflowColorHEX = toCSSColor(ReduxUpdater.selectedColor);

                xfdfAnnot.setColor(workflowColorHEX);
                xfdfAnnot.setTextColor(workflowColorHEX);

                if (xfdfAnnot.type === 'freetext') {
                    // transparent background
                    xfdfAnnot.removeColor();

                    // bordercolor
                    xfdfAnnot.setDefaultAppearanceColor(workflowColorHEX);

                    // textcolor
                    xfdfAnnot.setDefaultStyleColor(workflowColorHEX);
                    xfdfAnnot.setStyleColors(workflowColorHEX);
                }
            }

            xfdfAnnot.setId(uuidv4());
            xfdfAnnot.setAuthor(GlobalVariables.ownersName);
            xfdfAnnot.setPage(currentPage - 1);
            xfdfAnnot.setCreationDate();
            xfdfAnnot.setModifiedDate();

            pasteOffset.x += 10;
            pasteOffset.y += 10;
        });

        const xfdf = serializeXFDFDoc(xfdfDoc);
        devLog('modified XFDF', xfdf);

        await ApryseSDK.pdfNet.runWithCleanup(async () => {
            ApryseSDK.annotationManager.importAnnotations(xfdf).then(_ => {
                ApryseSDK.broadcastMessage = '';
            });
        }, GlobalVariables.licenseKey);

        ApryseSDK.setToolMode(ApryseSDK.ToolsENUM.ANNOTATION_EDIT);
        ReduxUpdater.annotationSidepanelRefresh();
    }

    static async pasteText() {
        const freeText =
            ApryseSDK.textAnnotationEditingContext as Core.Annotations.FreeTextAnnotation;

        if (!freeText) {
            devWarning('Pasting text failed, invalid annotation context.');
            return;
        }

        const editor = freeText.getEditor();
        const cursorSelection = editor.getSelection();

        const copiedText = await readFromClipboard();
        editor.deleteText(cursorSelection.index, cursorSelection.length);
        editor.insertText(cursorSelection.index, copiedText);
    }

    static rectMove(rect: Core.Math.Rect, x: number, y: number) {
        rect.translate(x, y);
        return rect;
    }

    static rectUnion(rect1: Core.Math.Rect, rect2: Core.Math.Rect): Core.Math.Rect {
        return new ApryseSDK._MathClass.Rect(
            Math.min(rect1.x1, rect2.x1),
            Math.min(rect1.y1, rect2.y1),
            Math.max(rect1.x2, rect2.x2),
            Math.max(rect1.y2, rect2.y2),
        );
    }

    static rectShiftVector(boundingBoxes: Core.Math.Rect[]): Core.Math.Point {
        const union = boundingBoxes.reduce((a, b) => ApryseSDK.rectUnion(a, b), boundingBoxes[0]);
        const center = union.getCenter();
        return new ApryseSDK._MathClass.Point(center.x, center.y);
    }

    static refreshViewer(specificPages?: number[]) {
        if (specificPages?.length) {
            [...new Set(specificPages)].forEach(page => ApryseSDK.documentViewer.refreshPage(page));
        } else {
            ApryseSDK.documentViewer.refreshAll();
        }
        ApryseSDK.documentViewer.updateView();
    }

    static selectAllText() {
        const freeText =
            ApryseSDK.textAnnotationEditingContext as Core.Annotations.FreeTextAnnotation;

        if (freeText) {
            const editor = freeText.getEditor();
            editor.setSelection(0, editor.getContents().length);
        }
    }

    static async setStamp(stampName: string) {
        const customStampData = (ReduxUpdater.lists.userStamps ?? []).find(userStamp => userStamp.id === stampName);
        if (customStampData) {
            await this._createStampTool.setRubberStamp(this._customStamps[stampName], '');
            return;
        }

        const height = 75;
        let width = 200;
        let color: Core.Annotations.Color | undefined = undefined;

        // Checkmark stamp is the only stamp to be square format and green.
        if (stampName === 'SHAccepted') {
            width = 75;
            color = ApryseSDK.Color(0, 152, 0);
        }

        this._createStampTool.useStamp({
            Icon: stampName,
            Width: width,
            Height: height,
            FillColor: color,
        });
    }

    static setToolMode(stateName: string) {
        if (!this._documentViewer || GlobalVariables.isTextEditing) {
            return;
        }

        if (stateName === ApryseSDK.ToolsENUM.PASTE_PREVIEW) {
            if (ReduxUpdater.viewMode !== PdfViewerViewModeFlag.Annotation) {
                GlobalDialogs.showNoty(
                    i18next.t('notifications.notInEditMode'),
                    ToastSeverity.Error,
                );
                return;
            }

            if (this.canSessionPaste) {
                ApryseSDK.pasteSessionAnnotations();
                return;
            }

            GlobalDialogs.showNoty(i18next.t('notifications.nothingToPaste'), ToastSeverity.Error);
            return;
        }

        const newTool = this._documentViewer.getTool(stateName);

        if (ReduxUpdater.useWorkflowColor && ApryseSDK.isFreeTextTool(newTool)) {
            ApryseSDK.instance.UI.disableElements(['colorPalette']);
        }

        if (newTool?.keyDown) {
            const DEL_KEY = 46;
            newTool.keyDown = keyDownEvent => {
                if (keyDownEvent.which === DEL_KEY) {
                    handleDeleteKey();
                }
            };
        }

        if (newTool?.addEventListener) {
            newTool.addEventListener('annotationCreated', ApryseSDK.onAnnotationCreated);
        }

        this._documentViewer.setToolMode(newTool);
        setTimeout(() => {
            // We need this for custom stamps placement to work properly when custom stamp is the first pick
            // Need to look into maybe fixing this in one of the large codebase refactorings
            this._documentViewer.setToolMode(newTool);
        }, 250);
    }

    static showPrintDialog(): void {
        this._instance.UI.print();
    }

    static stringToLineEndingStyle(style: string): Core.Annotations.LineEndType {
        switch (style.toLowerCase()) {
            case 'circle':
                return this._AnnotationsClass.LineEndType.CIRCLE;
            case 'closed':
            case 'closedarrow':
                return this._AnnotationsClass.LineEndType.CLOSED_ARROW;
            case 'diamond':
                return this._AnnotationsClass.LineEndType.DIAMOND;
            case 'butt':
            case 'line':
                return this._AnnotationsClass.LineEndType.BUTT;
            case 'line skewed':
            case 'slash':
                return this._AnnotationsClass.LineEndType.SLASH;
            case 'open':
            case 'openarrow':
                return this._AnnotationsClass.LineEndType.OPEN_ARROW;
            case 'ropenarrow':
            case 'reversed open':
            case 'reverseopenarrow':
                return this._AnnotationsClass.LineEndType.R_OPEN_ARROW;
            case 'rclosedarrow':
            case 'reversed closed':
            case 'reverseclosedarrow':
                return this._AnnotationsClass.LineEndType.R_CLOSED_ARROW;
            case 'square':
                return this._AnnotationsClass.LineEndType.SQUARE;
            default:
                return this._AnnotationsClass.LineEndType.NONE;
        }
    }

    static toDate(pdfNetDate: Core.PDFNet.Date) {
        return new Date(
            pdfNetDate.year,
            pdfNetDate.month,
            pdfNetDate.day,
            pdfNetDate.hour,
            pdfNetDate.minute,
            pdfNetDate.second,
            0,
        );
    }

    private static updateToolProperties<T extends ICommonTextProperties>(tool: T) {
        let textDecoration = `${ReduxUpdater.selectedFontAttributes.underline ? 'word' : ''} ${
            ReduxUpdater.selectedFontAttributes.strikeout ? 'line-through' : ''
        }`;
        if (textDecoration === ' ') {
            textDecoration = 'normal';
        }

        tool.defaults.Font = ReduxUpdater.selectedFontAttributes.family;
        tool.defaults.FontSize = ReduxUpdater.selectedFontAttributes.size;
        tool.defaults.TextAlign = ApryseSDK.decodeAlignment(
            ReduxUpdater.selectedFontAttributes.align,
        ) as 'left' | 'center' | 'right';

        if (tool instanceof ApryseSDK._ToolsClass.FreeTextCreateTool) {
            tool.defaults.RichTextStyle[0]['font-style'] = ReduxUpdater.selectedFontAttributes
                .italic
                ? 'italic'
                : 'normal';
            tool.defaults.RichTextStyle[0]['font-weight'] = ReduxUpdater.selectedFontAttributes.bold
                ? 'bold'
                : 'normal';
            tool.defaults.RichTextStyle[0]['text-decoration'] = textDecoration;
        }
    }

    static updateFreeTextFontStyles() {
        if (!this._documentViewer || !ReduxUpdater.selectedFontAttributes) {
            return;
        }

        const tool = this._documentViewer.getTool(ApryseSDK.ToolsENUM.CREATE_STAMP);
        if (tool instanceof ApryseSDK._ToolsClass.RubberStampCreateTool) {
            this._createStampTool = tool;
            ApryseSDK.updateUserStamps();
        }

        const textboxTool = this._documentViewer.getTool(ApryseSDK.ToolsENUM.CREATE_FREETEXT);
        if (textboxTool instanceof ApryseSDK._ToolsClass.FreeTextCreateTool) {
            ApryseSDK.updateToolProperties(textboxTool);
        }

        const typewriterTool = this._documentViewer.getTool(
            ApryseSDK.ToolsENUM.CREATE_FREETEXT_TYPEWRITER,
        );
        if (typewriterTool instanceof ApryseSDK._ToolsClass.FreeTextCreateTool) {
            ApryseSDK.updateToolProperties(typewriterTool);
        }

        const calloutTool = this._documentViewer.getTool(
            ApryseSDK.ToolsENUM.CREATE_FREETEXT_CALLOUT,
        );
        if (calloutTool instanceof ApryseSDK._ToolsClass.CalloutCreateTool) {
            ApryseSDK.updateToolProperties(calloutTool);
        }
    }

    static getAnnotationBasicInfo(annotation: Core.Annotations.Annotation) {
        return {
            type: ApryseSDK.annotPrettyType(annotation),
            author: annotation.Author,
            position: annotation.getRect(),
            size: [annotation.getHeight(), annotation.getWidth()],
        };
    }

    static async undo() {
        const lastAction = CustomHistoryManager.getPrevHistoryAction();

        if (!lastAction || !CustomHistoryManager.canUndo()) {
            devWarning('Cannot undo.');
            return;
        }

        CustomHistoryManager.undoRedoInProgress = true;

        CustomHistoryManager.undo(lastAction);
        ApryseSDK.refreshViewer(lastAction.annotations.map(a => a.PageNumber));

        RedliningActionLogger.appendLog('undo', lastAction.annotations);

        setTimeout(() => {
            CustomHistoryManager.undoRedoInProgress = false;
            ReduxUpdater.documentSaved = CustomHistoryManager.areAllAnnotationsSaved();
            ReduxUpdater.undoRedoRefresh();
            ReduxUpdater.annotationSidepanelRefresh();
            ReduxUpdater.ribbonRefresh();
        }, 50);
    }

    static async redo() {
        const nextAction = CustomHistoryManager.getNextHistoryAction();

        if (!nextAction || !CustomHistoryManager.canRedo()) {
            devWarning('Cannot redo.');
            return;
        }

        CustomHistoryManager.undoRedoInProgress = true;

        CustomHistoryManager.redo(nextAction);
        ApryseSDK.refreshViewer(nextAction.annotations.map(a => a.PageNumber));

        RedliningActionLogger.appendLog('redo', nextAction.annotations);

        setTimeout(() => {
            CustomHistoryManager.undoRedoInProgress = false;
            ReduxUpdater.documentSaved = CustomHistoryManager.areAllAnnotationsSaved();
            ReduxUpdater.undoRedoRefresh();
            ReduxUpdater.annotationSidepanelRefresh();
            ReduxUpdater.ribbonRefresh();
        }, 50);
    }

    static async loadUserStamps(): Promise<void> {
        try {
            const response = await fetch(backendApiUrl.stamps.all);

            if (!response.ok) {
                throw new Error('Failed to fetch user stamps.');
            }

            const stamps: IUserStamp[] = await response.json();
            ReduxUpdater.lists = {
                ...ReduxUpdater.lists,
                userStamps: stamps,
            };
        } catch (e) {
            devError(`Failed to load user stamps: ${e}`);
        }
    }

    static updateUserStamps() {
        if (!this._createStampTool) {
            return;
        }
        const stampList = ReduxUpdater.lists.userStamps ?? [];
        this._createStampTool.setStandardStamps(
            stampList.map(stamp => {
                // leading slash is important, so that SDK recognizes as URL (and not as URI)
                // if missing leading slash, SDK recognizes as URI and prepends "/ui/" subroute
                return '/' + backendApiUrl.stamps.image.replace('{id}', stamp.id);
            }),
        );
        this._createStampTool.getStandardStampAnnotations().then(data => {
            this._customStamps = {};
            data.forEach((m, i) => {
                this._customStamps[stampList[i].id] = m;
            });
        });
    }

    static selectAllAnnotations() {
        ApryseSDK.annotationManager.selectAnnotations(
            ApryseSDK.annotationManager.getAnnotationsList(),
        );
    }

    static updateSelections(selections: IPresetSelections) {
        if (
            !this._documentViewer ||
            !ReduxUpdater.stateName.startsWith('AnnotationCreate') ||
            ReduxUpdater.stateName === ApryseSDK.ToolsENUM.CREATE_STAMP
        ) {
            return;
        }

        const currentTool = this._documentViewer.getToolMode();

        if (
            currentTool.name &&
            [
                ApryseSDK.ToolsENUM.CREATE_MEASUREMENT_AREA,
                ApryseSDK.ToolsENUM.CREATE_MEASUREMENT_DISTANCE,
                ApryseSDK.ToolsENUM.CREATE_MEASUREMENT_PERIMETER,
            ].includes(currentTool.name)
        ) {
            currentTool.setStyles({
                FillColor: this.intToColor(0),
                StrokeColor: this.intToColor(selections.color),
                StrokeThickness: 1.0,
                TextColor: this.intToColor(1.0),
            });
            return;
        }

        currentTool.setStyles(oldStyles => {
            const newStyles = oldStyles;
            const lineStyle =
                currentTool.name === this.ToolsENUM.CREATE_POLYGON_CLOUD
                    ? selections.lineCloudyStyle
                    : selections.lineStyle;
            newStyles.FillColor = this.intToColor(selections.fillColor);
            newStyles.intensity = this.lineStyleToIntensity(lineStyle);
            newStyles.StrokeColor = this.intToColor(selections.color);
            newStyles.StrokeThickness =
                currentTool.name === this.ToolsENUM.CREATE_FREETEXT_TYPEWRITER
                    ? 0.0
                    : selections.lineWidth;
            newStyles.StrokeStyle = this.lineStyleToStyle(lineStyle, true);
            newStyles.Style = this.lineStyleToStyle(lineStyle);
            newStyles.TextColor = this.intToColor(selections.color);
            newStyles.EndLineStyle = this.stringToLineEndingStyle(
                currentTool.name === this.ToolsENUM.CREATE_ARROW
                    ? selections.arrowEnd
                    : selections.lineEnd,
            );
            newStyles.StartLineStyle = this.stringToLineEndingStyle(
                currentTool.name === this.ToolsENUM.CREATE_ARROW
                    ? selections.arrowStart
                    : selections.lineStart,
            );
            return newStyles;
        });
    }

    static getMeasureRatio(annotation: MeasurementAnnotation): {
        pageScale: { value: number; unit: string };
        worldScale: { value: number; unit: string };
    } {
        const coreScale = new this._instance.Core.Scale(annotation.Scale);

        return {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            pageScale: (coreScale as any)['gl'],
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            worldScale: (coreScale as any)['jl'],
        };
    }

    private static async extractDigitalSignatureField(
        objNum: number,
        subfilter: number,
        digitalSignatureField: Core.PDFNet.DigitalSignatureField,
    ) {
        devLog('Subfilter type: ' + subfilter);
        if (subfilter === this._pdfNetClass.DigitalSignatureField.SubFilterType.e_ETSI_RFC3161) {
            devLog('SubFilter == e_ETSI_RFC3161 (DocTimeStamp; no signing info)');
            return;
        }

        const signatureSigner = await digitalSignatureField.getSignatureName();
        const signingTime = await digitalSignatureField.getSigningTime();
        const signingTimeIsValid = await signingTime.isValid();
        const location = await digitalSignatureField.getLocation();
        const reason = await digitalSignatureField.getReason();
        const contactInfo = await digitalSignatureField.getContactInfo();
        if (signatureSigner) {
            this._signatureData[objNum].signer = signatureSigner;
        }
        this._signatureData[objNum].signingTime = this.toDate(signingTime);
        this._signatureData[objNum].isSigningTimeValid = signingTimeIsValid;
        this._signatureData[objNum].location = location;
        this._signatureData[objNum].reason = reason;
        this._signatureData[objNum].contactInfo = contactInfo;
        if (signingTime.year > 0) {
            this._signatureData[objNum].date = this.toDate(signingTime);
        }
        devLog(`Signature's signer: ${signatureSigner}`);

        if (await signingTime.isValid()) {
            devLog('Signing time is valid.');
        }

        devLog('Location: ' + location);
        devLog('Reason: ' + reason);
        devLog('Contact info: ' + contactInfo);
    }

    private static async extractTrustVerificationResult(
        objNum: number,
        hasTrustVerificationResult: boolean,
        verificationResult: Core.PDFNet.VerificationResult,
    ) {
        if (!hasTrustVerificationResult) {
            devLog('No detailed trust verification result available.');
            return;
        }

        const trustVerificationResult = await verificationResult.getTrustVerificationResult();
        const trustVerificationWasSuccessful = await trustVerificationResult.wasSuccessful();
        this._signatureData[objNum].trustVerificationResult = trustVerificationResult;
        this._signatureData[objNum].wasTrustVerificationResultSuccessful =
            trustVerificationWasSuccessful;
        devLog(trustVerificationWasSuccessful ? 'Trust verified.' : 'Trust not verifiable.');
        devLog(await trustVerificationResult.getResultString());

        const tmp_time_t = await trustVerificationResult.getTimeOfTrustVerification();
        const timeOfTrustVerificationENUM =
            await trustVerificationResult.getTimeOfTrustVerificationEnum();
        this._signatureData[objNum].timeOfTrustVerification = new Date(tmp_time_t * 1000);
        this._signatureData[objNum].timeOfTrustVerificationENUM = timeOfTrustVerificationENUM;
        if (tmp_time_t > 0) {
            this._signatureData[objNum].date = new Date(tmp_time_t * 1000);
        }

        const cert_path = await trustVerificationResult.getCertPath();
        if (cert_path.length === 0) {
            devLog('Could not print certificate path.');
            return;
        }

        devLog('Certificate path:');
        for (const full_cert of cert_path) {
            devLog('\tCertificate:');
            devLog('\t\tIssuer names:');
            const issuer_dn = await (await full_cert.getIssuerField()).getAllAttributesAndValues();
            if (issuer_dn.length > 0) {
                const strVals = await Promise.all(issuer_dn.map(o => o.getStringValue()));
                devLog('\t\t\t' + strVals);
                this._signatureData[objNum].signer = strVals[0];
            }
            devLog('\t\tSubject names:');
            const subject_dn = await (
                await full_cert.getSubjectField()
            ).getAllAttributesAndValues();
            if (subject_dn.length > 0) {
                const strVals = await Promise.all(subject_dn.map(o => o.getStringValue()));
                devLog('\t\t\t' + strVals);
            }
            devLog('\t\tExtensions:');
            const extension_dn = await full_cert.getExtensions();
            if (extension_dn.length > 0) {
                const strVals = await Promise.all(extension_dn.map(o => o.toString()));
                devLog('\t\t\t' + strVals);
            }
        }
    }

    private static async parseDigitalSignatureField(
        digitalSignatureField: Core.PDFNet.DigitalSignatureField,
        verificationOptions: Core.PDFNet.VerificationOptions,
    ) {
        const objNum = await (await digitalSignatureField.getSDFObj()).getObjNum();
        devLog(objNum);
        const result = await digitalSignatureField.verify(verificationOptions);
        const documentStatus = await result.getDocumentStatus();
        const verificationStatus = await result.getVerificationStatus();
        const unsupportedFeatures = await result.getUnsupportedFeatures();

        devLog(
            (verificationStatus
                ? 'Signature verified, objnum: '
                : 'Signature verification failed, objnum: ') + objNum,
        );

        this._signatureData[objNum] = {
            id: objNum,
            date: new Date(1970, 1, 1, 0, 0, 0, 0),
            signatureField: digitalSignatureField,
            signer: '',
            verificationResult: result,
            documentStatus: documentStatus,
            unsupportedFeatures: unsupportedFeatures,
            verificationStatus: verificationStatus,
        };

        devLog(this._signatureData);

        const subfilter = await digitalSignatureField.getSubFilter();
        await this.extractDigitalSignatureField(objNum, subfilter, digitalSignatureField);

        devLog((await digitalSignatureField.hasVisibleAppearance()) ? 'Visible' : 'Not visible');

        const digsigDocPermissions = await digitalSignatureField.getDocumentPermissions();
        const lockedFields = await digitalSignatureField.getLockedFields();
        this._signatureData[objNum].digsigDocPermissions = digsigDocPermissions;
        this._signatureData[objNum].lockedFields = lockedFields;

        const digestAlgorithm = await result.getDigestAlgorithm();
        const digestStatus = await result.getDigestStatus();
        const permissionsStatus = await result.getPermissionsStatus();
        this._signatureData[objNum].digestAlgorithm = digestAlgorithm;
        this._signatureData[objNum].digestStatus = digestStatus;
        this._signatureData[objNum].permissionsStatus = permissionsStatus;

        const changes = await result.getDisallowedChanges();
        this._signatureData[objNum].verificationResultDissallowedChanges = changes;

        // Get and print all the detailed trust-related results, if they are available.
        const hasTrustVerificationResult = await result.hasTrustVerificationResult();
        this._signatureData[objNum].hasTrustVerificationResult = hasTrustVerificationResult;
        await this.extractTrustVerificationResult(objNum, hasTrustVerificationResult, result);

        ReduxUpdater.annotationSidepanelRefresh();
    }

    static async verifyAllDigitalSignatures(in_public_key_file_paths: string[]) {
        this._signatureData = {};
        if (!this._pdfDoc) {
            return;
        }

        this._pdfDoc.initSecurityHandler();
        this._pdfDoc.lock();

        const opts = await this._pdfNetClass.VerificationOptions.create(
            this._pdfNetClass.VerificationOptions.SecurityLevel.e_compatibility_and_archiving,
        );

        // Add trust root to store of trusted certificates contained in VerificationOptions.
        // Use trust level corresponding to an identity trusted even for certification signatures.
        await Promise.all(
            in_public_key_file_paths.map(in_public_key_file_path =>
                opts.addTrustedCertificateFromURL(
                    in_public_key_file_path,
                    undefined,
                    this._pdfNetClass.VerificationOptions.CertificateTrustFlag.e_default_trust +
                        this._pdfNetClass.VerificationOptions.CertificateTrustFlag
                            .e_certification_trust,
                ),
            ),
        );

        // Iterate over the signatures and verify all of them.
        const digsig_fitr = await this._pdfDoc.getDigitalSignatureFieldIteratorBegin();
        for (; await digsig_fitr.hasNext(); await digsig_fitr.next()) {
            const digsigfield = await digsig_fitr.current();
            await this.parseDigitalSignatureField(digsigfield, opts);
        }
    }

    static async removeAllDigitalSignatures(pdfDoc: Core.PDFNet.PDFDoc) {
        if (!pdfDoc) {
            return;
        }

        pdfDoc.initSecurityHandler();
        pdfDoc.lock();
        const digsig_fitr = await pdfDoc.getDigitalSignatureFieldIteratorBegin();
        for (; await digsig_fitr.hasNext(); await digsig_fitr.next()) {
            const digsigfield = await digsig_fitr.current();
            digsigfield.clearSignature();
        }
    }

    static loadUserCategories() {
        const userCategories: StampCategory[] = [];
        if (ReduxUpdater.lists?.userStamps) {
            ReduxUpdater.lists.userStamps.forEach(stamp => {
                if (!userCategories.some(category => category.title === stamp.category)) {
                    const userCategory =
                        ApryseSDK.userCategoriesList[
                            ApryseSDK.userCategoriesList.findIndex(
                                ctg => ctg.title === stamp.category,
                            )
                        ];
                    userCategories.push({
                        title: stamp.category,
                        isChecked: userCategory?.isChecked ?? true,
                    });
                }
            });
        }
        userCategories.sort((a, b) => a.title.localeCompare(b.title));

        // Empty the readonly array
        ApryseSDK.userCategoriesList.splice(0, ApryseSDK.userCategoriesList.length);

        // Fill the readonly array
        ApryseSDK.userCategoriesList.push(...userCategories);
    }
}
